import {
    Chip, Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    Typography, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { iconSize } from 'assets/theme/iconSize';
import { setLoading, useMaterialUIController } from 'context';
import { ITableProps } from '@types_main/itable.props';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { CustomFieldResponse } from '../services/responses/custom-field.response';
import { CustomFieldService } from '../services/custom-field.service';
import { ECustomFieldType } from '../services/responses/custom-field.response';
import { TYPE_KEY } from '../custom-field-value';

const COLUMNS = 6;

export function CustomFieldTable(props: ITableProps<CustomFieldResponse>) {
    const { t } = useTranslation();
    const { data, editAction } = props;
    const [controller, dispatch] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();

    function doDelete(id: string) {
        setLoading(dispatch, true);
        new CustomFieldService()
            .deleteById(id)
            .then(() => {
                showAlert(t('camposPersonalizados.excluirSucesso'), 'success', 2500);
                props.setRefresh(!props.refresh);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000))
            .finally(() => setLoading(dispatch, false));
    }

    return (
        <Grid>
            <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="15%">{t('general.nome')}</TableCell>
                            <TableCell width="9%">{t('camposPersonalizados.tipo')}</TableCell>
                            <TableCell width="14%" align="center">{t('camposPersonalizados.obrigatorio')}</TableCell>
                            <TableCell width="14%" align="center">{t('camposPersonalizados.situacao')}</TableCell>
                            <TableCell width="10%" align="center">{t('camposPersonalizados.ordem')}</TableCell>
                            <TableCell width="12%" align="center">{t('general.acoes')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.data ? (
                            data.data.records.length > 0 ? (
                                data.data.records.map((item) => (
                                    <TableRow hover key={item.id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell sx={{ maxWidth: 0 }}>
                                            {t(TYPE_KEY[item.type])}
                                            {item.type === ECustomFieldType.List && item.options.length > 0 && (
                                                <Tooltip title={item.options.join(', ')}>
                                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                        {item.options.join(', ')}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.required ? t('camposPersonalizados.sim') : t('camposPersonalizados.nao')}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={item.active ? 'success' : 'default'}
                                                label={item.active ? t('general.ativo') : t('camposPersonalizados.arquivado')}
                                                sx={{ '& .MuiChip-label': { color: cc.onTint } }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{item.sortOrder}</TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <Tooltip title={t('general.editar')}>
                                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(item.id)}>
                                                        <EditIcon
                                                            style={iconSize}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('general.excluir')}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.25 }}
                                                        onClick={() =>
                                                            alertDelete(
                                                                item.id,
                                                                doDelete,
                                                                item.name,
                                                                t('camposPersonalizados.excluirConfirm')
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon
                                                            sx={iconSize}
                                                            color="error"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow hover>
                                    <TableCell colSpan={COLUMNS} align="center">
                                        {t('general.nenhumRegistro')}
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            Array.from({ length: 5 }, (_, i) => i).map((index) => (
                                <TableRow hover key={index}>
                                    {Array.from({ length: COLUMNS }, (_, cell) => (
                                        <TableCell key={cell}>
                                            <Skeleton height={30} variant="rectangular" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
